import React, { useState } from 'react';
import { usePwaInstall } from '../hooks/usePwaInstall';
import './PickingInstallBanner.css';

/**
 * Install prompt for the picking mobile PWA.
 * Rendered only for picking touch viewports in browser (not standalone) mode.
 *
 * - Android/Chrome: button fires the native install sheet
 * - iOS Safari: Share → Add to Home Screen instructions
 * - Already installed (best-effort detection): reminder to open from home screen
 * - Dismiss hides for the session; next session shows a compact pill instead
 */
function PickingInstallBanner({ darkMode }) {
  const {
    standalone,
    platform,
    alreadyInstalled,
    promptInstall,
    openInstalledApp,
    dismiss,
    dismissedThisSession,
    dismissedBefore,
  } = usePwaInstall();
  const [expanded, setExpanded] = useState(false);

  if (standalone || dismissedThisSession) return null;
  if (!platform && !alreadyInstalled) return null;

  const compact = dismissedBefore && !expanded;
  const cls = `PickingInstallBanner${darkMode ? ' PickingInstallBanner--dark' : ''}`;

  if (compact) {
    return (
      <div className={`${cls} PickingInstallBanner--pill`}>
        <button
          type="button"
          className="PickingInstallBanner-pill-btn"
          onClick={alreadyInstalled ? openInstalledApp : () => setExpanded(true)}
        >
          {alreadyInstalled ? 'Open Picking app' : 'Install Picking app'}
        </button>
        <button
          type="button"
          className="PickingInstallBanner-close"
          aria-label="Hide install prompt"
          onClick={dismiss}
        >
          ×
        </button>
      </div>
    );
  }

  let title;
  let body;
  let action = null;

  if (alreadyInstalled) {
    title = 'Picking app is installed';
    body = 'Open the full-screen app instead of this browser tab.';
    action = (
      <button
        type="button"
        className="PickingInstallBanner-install-btn"
        onClick={openInstalledApp}
      >
        Open app
      </button>
    );
  } else if (platform === 'android') {
    title = 'Install the Picking app';
    body = 'Runs full screen — no browser bar, more room for picking.';
    action = (
      <button
        type="button"
        className="PickingInstallBanner-install-btn"
        onClick={promptInstall}
      >
        Install app
      </button>
    );
  } else {
    // iOS — no install API, show Add to Home Screen steps
    title = 'Add Picking to your home screen';
    body = (
      <>
        Tap the <span className="PickingInstallBanner-ios-icon" aria-label="Share">
          <svg viewBox="0 0 16 20" width="12" height="15" aria-hidden="true">
            <path d="M8 1v12M8 1L4.5 4.5M8 1l3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 8H1.8v10.4h12.4V8H13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </span> Share button, then <strong>Add to Home Screen</strong>. It runs full screen with more room for picking.
      </>
    );
  }

  return (
    <div className={cls} role="dialog" aria-label="Install picking app">
      <img
        className="PickingInstallBanner-icon"
        src="/icons/icon-192.png"
        alt=""
        width="38"
        height="38"
      />
      <div className="PickingInstallBanner-text">
        <div className="PickingInstallBanner-title">{title}</div>
        <div className="PickingInstallBanner-body">{body}</div>
      </div>
      {action}
      <button
        type="button"
        className="PickingInstallBanner-close"
        aria-label="Dismiss install prompt"
        onClick={dismiss}
      >
        ×
      </button>
    </div>
  );
}

export default PickingInstallBanner;
